import {
  Dispatch,
  forwardRef,
  SetStateAction,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import logo from "../../../asset/images/logo/logo.svg";
import * as s from "../InputPage.styles";
import { AnalysisForm, AreaUnit, areaUnitList, cropList } from "../machine";
import {
  formatManToEokMan,
  formatPriceNumber,
  fromM2,
  toM2,
} from "../function";
import { useFormattedNumberField } from "./useFormattedNumberField";
import CropSection from "./CropSection";
import CostSection from "./CostSection";
import WorkDaysSection from "./WorkDaysSection";
import FarmingAreaSection from "./FarmingAreaSection";
import FarmingAgentSection from "./FarmingAgentSection";

export interface LandInfoHandle {
  validateAndScroll: () => boolean;
}

interface Props {
  temp: React.RefObject<HTMLDivElement>;
  formData: AnalysisForm;
  setFormData: Dispatch<SetStateAction<AnalysisForm>>;
  handleNext: () => void;
}

const LandInfo = forwardRef<LandInfoHandle, Props>(function LandInfo(
  { temp, formData, setFormData, handleNext },
  ref
) {
  const [isCommunal, setIsCommunal] = useState(!!formData.area);

  //  필수 항목에 대한 ref
  const allAreaRef = useRef<HTMLDivElement | null>(null);
  const areaRef = useRef<HTMLDivElement | null>(null);
  const quantityRef = useRef<HTMLDivElement | null>(null);
  const salesRef = useRef<HTMLDivElement | null>(null);
  const agentRevenueRef = useRef<HTMLDivElement | null>(null);

  const scrollToRef = (r: React.RefObject<HTMLElement>) => {
    r.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const validateAndScroll = () => {
    // 전체 면적
    if (!formData.allArea || formData.allArea <= 0) {
      scrollToRef(allAreaRef);
      return false;
    }

    // 자경 면적
    if (isCommunal && (!formData.area || formData.area <= 0)) {
      scrollToRef(areaRef);
      return false;
    }

    // 연 생산량
    if (!formData.quantity || formData.quantity <= 0) {
      scrollToRef(quantityRef);
      return false;
    }

    // 연 매출
    if (!formData.sales || formData.sales <= 0) {
      scrollToRef(salesRef);
      return false;
    }

    // 영농대행 연 매출
    // if (
    //   isCommunal &&
    //   (!formData.farmAgentRevenue || formData.farmAgentRevenue <= 0)
    // ) {
    //   scrollToRef(agentRevenueRef);
    //   return false;
    // }

    return true;
  };

  // 부모에서 ref로 호출할 수 있도록 노출
  useImperativeHandle(ref, () => ({
    validateAndScroll,
  }));

  const handleClickNext = () => {
    if (!validateAndScroll()) return;
    handleNext();
  };

  return (
    <s.Container>
      <s.Header>
        <s.StepWrapper>
          <s.Step $active>Step 1</s.Step>
          <s.StepDivider />
          <s.Step>Step 2</s.Step>
          <s.StepDivider />
          <s.Step>Step 3</s.Step>
        </s.StepWrapper>
        <s.Title>영농 정보 입력하기</s.Title>
        <s.Address>
          {JSON.parse(sessionStorage.areaList)[0].area || ""}
        </s.Address>
      </s.Header>
      <s.Content>
        <s.GreenBox>
          <img src={logo} alt="logo" />
          <div>
            내 영농 정보와 트랙터/부속기에 따라 작업 효율, 경제성 등을 분석할 수
            있습니다.
          </div>
        </s.GreenBox>

        {/* 1. 영농 면적 */}
        <FarmingAreaSection
          formData={formData}
          setFormData={setFormData}
          isCommunal={isCommunal}
          setIsCommunal={setIsCommunal}
          allAreaRef={allAreaRef}
          areaRef={areaRef}
        />

        {/* 1-1 영농대행 */}
        {isCommunal && (
          <FarmingAgentSection
            formData={formData}
            setFormData={setFormData}
            agentRevenueRef={agentRevenueRef}
          />
        )}

        {/* 2. 작물 */}
        <CropSection
          formData={formData}
          setFormData={setFormData}
          isCommunal={isCommunal}
          quantityRef={quantityRef}
          salesRef={salesRef}
        />

        {/* 3. 작업 가능일 */}
        <WorkDaysSection formData={formData} setFormData={setFormData} />

        {/* 4. 비용 */}
        <CostSection formData={formData} setFormData={setFormData} />

        <s.NextButton onClick={handleClickNext}>다음으로</s.NextButton>
      </s.Content>
    </s.Container>
  );
});

export default LandInfo;
